import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Upload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Super Admin — Master Club List (Iter 90).
 *
 *  1. Upload the RI-published master club XLSX (`Club ID`, `Club Name`,
 *     optional `Zone`, `District Number`). The backend upserts on `club_id`.
 *  2. Run "Preview normalisation" — for every distinct `persons.club_name`
 *     variant currently in the DB, the API returns:
 *        - `exact`   — safe auto-mappings (case + whitespace-only diff)
 *        - `suggestions` — fuzzy top-3 matches per unknown variant
 *        - `unmatched` — user picks manually
 *  3. User approves a mapping per row → "Apply" rewrites every affected
 *     `persons.club_name` to canonical + sets `persons.club_id`.
 */
export default function MasterClubs() {
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [clubs, setClubs] = useState([]);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [applyBusy, setApplyBusy] = useState(false);
    const [preview, setPreview] = useState(null);          // full preview response
    const [approvals, setApprovals] = useState({});        // variant -> club_id chosen
    const [q, setQ] = useState("");
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/super/clubs");
            setClubs(data?.clubs || []);
            setTotal(data?.total || 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load clubs");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const upload = async (file) => {
        if (!file) return;
        setUploadBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/super/clubs/upload", fd);
            toast.success(
                `Master list loaded — ${data.inserted} added, ${data.updated} updated, ${data.skipped} skipped`
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const runPreview = async () => {
        setPreviewBusy(true);
        try {
            const { data } = await api.get("/super/clubs/normalize/preview");
            setPreview(data);
            // Auto-accept exact matches so the Apply payload is complete
            const initial = {};
            for (const e of (data.exact || [])) initial[e.variant] = e.club_id;
            for (const s of (data.suggestions || [])) {
                if (s.auto_pick_ok && s.candidates?.[0]) {
                    initial[s.variant] = s.candidates[0].club_id;
                }
            }
            setApprovals(initial);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setPreviewBusy(false);
        }
    };

    const apply = async () => {
        if (Object.keys(approvals).length === 0) {
            toast.info("Nothing to apply — accept at least one mapping first");
            return;
        }
        if (!window.confirm(
            `Rewrite ${Object.keys(approvals).length} club variants in the persons collection?\n\n` +
            `This sets club_name to the canonical RI name and stamps club_id on every matched person. ` +
            `Idempotent — safe to re-run.`
        )) return;
        setApplyBusy(true);
        try {
            const { data } = await api.post("/super/clubs/normalize/apply", { mapping: approvals });
            toast.success(`${data.total_updated} persons updated across ${data.variants?.length || 0} variants`);
            await runPreview();  // refresh
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Apply failed");
        } finally {
            setApplyBusy(false);
        }
    };

    const filtered = clubs.filter((c) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (c.club_name || "").toLowerCase().includes(s) ||
               (c.club_id || "").includes(s);
    });

    return (
        <div className="min-h-screen bg-rotary-cream p-4 sm:p-8">
            <div className="mx-auto max-w-6xl space-y-6">
                <div className="flex items-center gap-2 mb-2">
                    <Button variant="ghost" size="sm" asChild data-testid="clubs-back-btn">
                        <Link to="/admin"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Link>
                    </Button>
                </div>
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-rotary-ink">Master Club List</h1>
                    <p className="text-rotary-ink-muted text-sm mt-1">
                        Upload the RI-published club roster, then normalise every free-text
                        <code className="mx-1 px-1 rounded bg-rotary-cream-alt">club_name</code>
                        in the persons collection against it.
                    </p>
                </div>

                {/* Upload */}
                <Card className="p-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            data-testid="clubs-upload-input"
                            onChange={(e) => upload(e.target.files?.[0])}
                        />
                        <Button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploadBusy}
                            data-testid="clubs-upload-btn"
                        >
                            {uploadBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
                            Upload master XLSX
                        </Button>
                        <span className="text-sm text-rotary-ink-muted">
                            Columns: <code>Club ID</code>, <code>Club Name</code>, optional <code>Zone</code>, <code>District Number</code>
                        </span>
                        <span className="ml-auto text-sm text-rotary-ink">
                            {loading ? "…" : <><strong>{total}</strong> clubs in master</>}
                        </span>
                    </div>
                </Card>

                {/* Normalisation */}
                <Card className="p-4 space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-lg font-semibold text-rotary-ink">Normalise member clubs</h2>
                        <Button
                            variant="outline" size="sm"
                            onClick={runPreview}
                            disabled={previewBusy || total === 0}
                            data-testid="clubs-preview-btn"
                        >
                            {previewBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                            Preview normalisation
                        </Button>
                        <Button
                            size="sm"
                            onClick={apply}
                            disabled={applyBusy || !preview || Object.keys(approvals).length === 0}
                            data-testid="clubs-apply-btn"
                        >
                            {applyBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                            Apply mapping ({Object.keys(approvals).length})
                        </Button>
                    </div>
                    {preview && (
                        <div className="text-sm space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <Stat label="Total variants" value={preview.total_variants} />
                                <Stat label="Exact matches" value={preview.exact_count} ok />
                                <Stat label="Suggestions" value={preview.suggestion_count} />
                                <Stat label="Unmatched" value={preview.unmatched_count} warn />
                            </div>
                            <SuggestionList
                                items={preview.suggestions || []}
                                approvals={approvals}
                                setApprovals={setApprovals}
                            />
                            <UnmatchedList
                                items={preview.unmatched || []}
                                master={clubs}
                                approvals={approvals}
                                setApprovals={setApprovals}
                            />
                        </div>
                    )}
                </Card>

                {/* Master list browser */}
                <Card className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold text-rotary-ink">Master list</h2>
                        <Input
                            value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name or ID…"
                            className="max-w-sm"
                            data-testid="clubs-search-input"
                        />
                        <span className="text-sm text-rotary-ink-muted ml-auto">
                            Showing {filtered.length} of {clubs.length}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b">
                                    <th className="py-2 px-2">Club ID</th>
                                    <th className="py-2 px-2">Club Name</th>
                                    <th className="py-2 px-2">District</th>
                                    <th className="py-2 px-2">Zone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.slice(0, 200).map((c) => (
                                    <tr key={c.club_id} className="border-b hover:bg-rotary-cream-alt/40">
                                        <td className="py-1.5 px-2 font-mono text-xs">{c.club_id}</td>
                                        <td className="py-1.5 px-2">{c.club_name}</td>
                                        <td className="py-1.5 px-2">{c.district_number || ""}</td>
                                        <td className="py-1.5 px-2">{c.zone || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
}

function Stat({ label, value, ok, warn }) {
    return (
        <div className={`rounded-lg border p-2 ${ok ? "border-emerald-300 bg-emerald-50" : warn ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="text-xs text-rotary-ink-muted uppercase tracking-wide">{label}</div>
            <div className="text-xl font-semibold">{value}</div>
        </div>
    );
}

function SuggestionList({ items, approvals, setApprovals }) {
    if (!items.length) return null;
    return (
        <div className="space-y-2">
            <h3 className="text-sm font-semibold text-rotary-ink flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-amber-500" /> Fuzzy suggestions ({items.length})
            </h3>
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {items.map((s) => (
                    <div key={s.variant} className="rounded-md border border-slate-200 p-2 flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="font-medium truncate" title={s.variant}>{s.variant}</div>
                            <div className="text-xs text-rotary-ink-muted">{s.count} member{s.count === 1 ? "" : "s"}</div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                            {(s.candidates || []).map((c) => (
                                <button
                                    key={c.club_id}
                                    onClick={() => setApprovals({ ...approvals, [s.variant]: c.club_id })}
                                    className={`text-xs px-2 py-1 rounded border transition ${approvals[s.variant] === c.club_id
                                        ? "bg-emerald-500 text-white border-emerald-500"
                                        : "bg-white hover:bg-emerald-50 border-slate-300"}`}
                                    data-testid={`clubs-approve-${s.variant.replace(/\s+/g, "-")}-${c.club_id}`}
                                >
                                    {approvals[s.variant] === c.club_id && <CheckCircle2 className="inline h-3 w-3 mr-1" />}
                                    {c.club_name} <span className="opacity-60 ml-1">{c.score}%</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function UnmatchedList({ items, master, approvals, setApprovals }) {
    if (!items.length) return null;
    return (
        <div className="space-y-2">
            <h3 className="text-sm font-semibold text-rotary-ink flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-red-500" /> Unmatched ({items.length}) — pick a canonical club or leave blank
            </h3>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {items.map((u) => (
                    <div key={u.variant} className="rounded-md border border-slate-200 p-2 flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="font-medium truncate" title={u.variant}>{u.variant}</div>
                            <div className="text-xs text-rotary-ink-muted">{u.count} member{u.count === 1 ? "" : "s"}</div>
                        </div>
                        <select
                            value={approvals[u.variant] || ""}
                            onChange={(e) => {
                                const next = { ...approvals };
                                if (e.target.value) next[u.variant] = e.target.value;
                                else delete next[u.variant];
                                setApprovals(next);
                            }}
                            className="text-sm border rounded px-2 py-1 max-w-xs"
                            data-testid={`clubs-manual-pick-${u.variant.replace(/\s+/g, "-")}`}
                        >
                            <option value="">— Skip —</option>
                            {master.map((m) => (
                                <option key={m.club_id} value={m.club_id}>
                                    {m.club_name} ({m.club_id})
                                </option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
        </div>
    );
}
